/**
 * Strings for email accounts (2026-10-28,
 * docs/decisions/2026-10-28-email-accounts.md): sign-in and sign-up by
 * email, verification, adding an email to an account made before email
 * sign-in, changing it, and forgot password.
 */
import type { Dict } from './core';

export const emailEn: Dict = {
  // sign-in and sign-up
  'auth.email.label': 'Email',
  'auth.email.help': 'We’ll send a code to verify it. Only you see it.',
  'auth.email.invalid': 'Enter a full email address, like name@example.com.',
  'auth.email.taken':
    'An account already uses this email. Sign in, or choose “Forgot your password?”.',
  'auth.email.unchanged': 'That’s already your email.',
  'auth.signin.loginHelp':
    'Email not verified yet, or made your account before email sign-in? You can use your username.',
  'auth.username.handle': 'Your public @handle: friends find and mention you with it.',
  'auth.forgot.link': 'Forgot your password?',
  // forgot password
  'auth.forgot.title': 'Reset your password',
  'auth.forgot.body':
    'Enter your account’s email. If it’s verified, we’ll send a link to choose a new password.',
  'auth.forgot.send': 'Send the link',
  'auth.forgot.sentTitle': 'Check your email',
  'auth.forgot.sent':
    'If an account with a verified email matches {email}, a reset link is on its way. It works for 1 hour.',
  'auth.forgot.noEmail': 'No verified email, or lost two-step? Use a recovery code instead.',
  'auth.forgot.again': 'Send again',
  'auth.forgot.wait':
    'Many reset links were asked for in the last hour. If this address belongs to an account with a verified email, the links sent earlier still work: check its inbox (and spam). Or try again in {n} minutes.',
  // the reset link
  'auth.reset.title': 'Choose a new password',
  'auth.reset.body': 'This signs you out on every device. Two-step stays on.',
  'auth.reset.submit': 'Set the new password',
  'auth.reset.doneTitle': 'Password changed',
  'auth.reset.done':
    'Your password is changed and every device was signed out. Sign in with the new one.',
  'auth.reset.newLink': 'Get a new link',
  // the verification link
  'email.link.verified': 'Your email is verified.',
  'email.link.changed': 'Your new email is confirmed. Sign in with it from now on.',
  'email.link.checking': 'Checking the link…',
  // the banner
  'email.banner.title': 'Verify your email',
  'email.banner.body': 'We sent a 6-digit code to {email}.',
  'email.banner.enter': 'Enter code',
  'email.resend': 'Resend',
  'email.resent': 'A new code is on its way to {email}.',
  'email.change': 'Change email',
  // entering the code
  'email.verify.title': 'Enter the code',
  'email.verify.body':
    'We sent a 6-digit code to {email}. It works for 24 hours. You can also open the link in that email.',
  'email.verify.code': 'Verification code',
  'email.verify.submit': 'Verify',
  'email.verify.changed': 'Your email is now {email}.',
  'email.later': 'Later',
  // changing it
  'email.changeTitle': 'Change your email',
  'email.changeBody':
    'We’ll send a code to the new address. Your current email stays until you confirm, and we’ll tell it about the change.',
  'email.changeBodyUnverified':
    'Your email isn’t verified yet, so the new one replaces it now. We’ll send a code to it.',
  'email.new': 'New email',
  'email.changeSubmit': 'Send the code',
  // Settings › Security
  'email.title': 'Email',
  'email.sub': 'You sign in with it, and password resets go to it. Only you see it.',
  'email.verified': 'Verified',
  'email.unverified': 'Not verified',
  'email.show': 'Show email',
  'email.hide': 'Hide email',
  'email.pending': 'Waiting for confirmation: {email}',
  'email.pendingSub': 'Your current email stays until you enter the code we sent to the new one.',
  'email.cancelChange': 'Cancel change',
  'email.cancelled': 'Change cancelled. Your email stays {email}.',
  'email.none': 'No email yet',
  'email.add': 'Add email',
  // the "Add your email" step (accounts made before email sign-in)
  'email.add.title': 'Add your email',
  'email.add.body':
    'Neosocial now signs you in with your email, and uses it to help you back in if you forget your password. Add one to continue.',
  'email.add.after': 'Once it’s verified, you’ll sign in with this email instead of your username.',
  'email.add.submit': 'Continue',
  'email.add.password': 'For your safety, enter your password too.',
  'email.add.code': 'Two-step is on: enter its code too.',
  'email.add.locked':
    'Your account was recovered recently, so for your safety an email can be added from {date}. Until then, keep signing in with your username.',
  'email.add.continue': 'Continue for now',
  // server refusals (errors.ts)
  'ns.err.100007': 'Enter your two-step code too.',
  'ns.err.100010': 'For your safety, your email can’t be changed for 7 days after a recovery.',
  'ns.err.100010.until': 'For your safety, your email can’t be changed until {date}.',
  'ns.err.100040': 'That code isn’t right. Check the newest email and try again.',
  'ns.err.100041': 'This code or link has expired or was already used. Send a new one.',
  'ns.err.100042': 'Add an email to your account first.',
  'ns.err.100043': 'This reset link has expired or was already used. Ask for a new one.',
  'ns.err.100044': 'The email couldn’t be sent right now. Try again in a few minutes.',
};
